import { AxiosInstance } from "axios";
import { useMutation, useQuery } from "react-query";
import { useFetchContext } from "../FetchContext";
import { toast } from "react-toastify";
import { urls } from "../urls";

interface PostFileProps {
  authAxios: AxiosInstance;
  file: File;
}

interface PostCrateFileProps {
  authAxios: AxiosInstance;
  file: File;
  crate: string;
  label_name: string;
  notes: string;
}

interface PostReportTemplateUploadProps {
  authAxios: AxiosInstance;
  file: File;
  report_id: number;
  workorder_id: number;
  // user_id: number;
  datetime: string | null;
}

interface PostModuleFileProps {
  authAxios: AxiosInstance;
  file: File;
  moduleintake: string;
  label_name: string;
  notes: string;
  intake_date: string;
  received_date: string;
}
export interface PostParseDocumentProps {
  authAxios: any;
  file: File;
  report_type_id: number;
  pqp_version:string
}
export interface PostChecklistProps {
  authAxios: any;
  file: File;
  report_type_definition_id: number;
  version: string
}


export const postFile = async ({
  authAxios,
  file,
}: PostFileProps): Promise<any> => {
  try {
    const formData = new FormData();

    formData.append("file", file, file.name);
    formData.append("hash_algorithm", "md5");
    formData.append("blob_container", "test");
    const { data } = await authAxios.post(`azure_files/`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return data;
  } catch (err) {
    const error =
      err.response.data instanceof Array
        ? err.response.data.join()
        : err.response.data;
    throw new Error(`Error while uploading file(s):${error}`);
  }
};

export const usePostFile = () => {
  return useMutation(postFile);
};

export const postCrateFile = async ({
  authAxios,
  file,
  crate,
  label_name,
  notes,
}: PostCrateFileProps): Promise<any> => {
  try {
    const formData = new FormData();

    formData.append("image_path", file, file.name);
    formData.append("newcrateintake", crate);
    formData.append("label_name", label_name);
    formData.append("notes", notes);

    const { data } = await authAxios.post(
      `crate_intake_images/upload_image/`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return data;
  } catch (err) {
    const error =
      err.response.data instanceof Array
        ? err.response.data.join()
        : err.response.data;
    throw new Error(`Error while uploading file(s):${error}`);
  }
};

export const usePostCrateFile = () => {
  return useMutation(postCrateFile);
};

export const postModuleFile = async ({
  authAxios,
  file,
  moduleintake,
  label_name,
  notes,
  intake_date,
  received_date,
}: PostModuleFileProps): Promise<any> => {
  try {
    const formData = new FormData();
    formData.append("image_path", file, file.name);
    formData.append("moduleintake", moduleintake);
    formData.append("label_name", label_name);
    formData.append("notes", notes);
    formData.append("intake_date", intake_date);
    formData.append("received_date", received_date);
    const { data } = await authAxios.post(
      `module_intake_images/upload_image/`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return data;
  } catch (err) {
    const error =
      err.response.data instanceof Array
        ? err.response.data.join()
        : err.response.data;
    throw new Error(`Error while uploading file(s):${error}`);
  }
};

export const usePostModulesFile = () => {
  return useMutation(postModuleFile);
};
export const useDownloadImageFromUrl = (url: string, name: string) => {
  const { authAxiosFromUrl } = useFetchContext();

  const downloadImageFromUrl = async (): Promise<any> => {
    const { data } = await authAxiosFromUrl.get(url, {
      responseType: "blob",
    });
    const downloadUrl = URL.createObjectURL(data);
    let a = document.createElement("a");
    a.href = downloadUrl;
    a.download = name;
    document.body.appendChild(a);
    a.click();
  };
  return useQuery(["downloadImageFromUrl", url, name], downloadImageFromUrl, {
    enabled: false,
  });
};

export const useDownloadImageFromID = (id: string, name: string) => {
  const { authAxios } = useFetchContext();

  const downloadImageFromID = async (): Promise<any> => {
    const { data } = await authAxios.get(`azure_files/${id}/download/`, {
      responseType: "blob",
    });
    const downloadUrl = URL.createObjectURL(data);
    let a = document.createElement("a");
    a.href = downloadUrl;
    a.download = name;
    document.body.appendChild(a);
    a.click();
  };
  return useQuery(["downloadImageFromID", id], downloadImageFromID, {
    enabled: name !== null && name !== "" && name !== undefined,
  });
};

export const postReportTemplateUpload = async ({
  authAxios,
  file,
  report_id,
  workorder_id,
  // user_id,
  datetime,
}: PostReportTemplateUploadProps): Promise<any> => {
  try {
    const formData = new FormData();
    formData.append("file", file, file.name);
    formData.append("report", report_id.toString());
    formData.append("workorder", workorder_id.toString());
    // formData.append("user_id", user_id.toString());
    formData.append("datetime", datetime ?? "");

    const { data } = await authAxios.post(
      `reportfiletemplate/upload/`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return data;
  } catch (err: any) {
    let errorMessage = "Error while uploading file(s)";

    if (Array.isArray(err?.response?.data)) {
      errorMessage = err.response.data.join();
    } else if (
      typeof err?.response?.data === "object" &&
      err.response.data?.msg
    ) {
      errorMessage = err.response.data.msg;
    } else if (err?.message) {
      errorMessage = err.message;
    }

    throw new Error(errorMessage);
  }
};

export const usePostReportTemplateUpload = () => {
  return useMutation(postReportTemplateUpload);
};

export const useEdgePinchFilePost = () => {
  return useMutation(edgePinchFiles);
};

export const edgePinchFiles = async ({
  authAxios,
  selectedFile,
  asset_id,
  // new_visualizer,
}: any): Promise<any> => {
  if (!selectedFile) {
    throw new Error("No file provided for upload.");
  }
  try {
    const formData = new FormData();
    formData.append("file", selectedFile); // The server usually wants 'file'
    formData.append("asset_id", asset_id.toString());
    // formData.append("new_visualizer", new_visualizer);

    const { data } = await authAxios.post(
      `edge_pinch_upload/upload_excel/`,
      formData,
    );
    return data;
  } catch (err: any) {
    const error =
      err?.response?.data?.error ||
      err?.response?.data?.message ||
      err?.message ||
      "Something went wrong";

    const details = err?.response?.data?.details;

    const detailMessage = Array.isArray(details) ? details.join(", ") : details;

    toast.error(detailMessage ? `${error}: ${detailMessage}` : error);
  }
};

export const useHailFilePost = () => {
  return useMutation(postHailFiles);
};

export const postHailFiles = async ({
  authAxios,
  selectedFile,
  procedure_result_ids,
}: any): Promise<any> => {
  try {
    const formData = new FormData();
    formData.append("file", selectedFile, selectedFile.name.toString());
    formData.append("procedure_result_ids", procedure_result_ids);

    const { data } = await authAxios.post(
      `/hailtestcalculation/upload_hail_file/`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return data;
  } catch (err: any) {
    // Extract the raw server error payload, or build a clean fallback object
    const errorData = err.response?.data || {
      message: err.message || "File upload failed.",
    };

    // Throwing the object directly preserves the .message and .errors properties
    throw errorData;
  }
};

export const useUpdateEdgePinchFilePost = () => {
  return useMutation(UpdateedgePinchFiles);
};

export const UpdateedgePinchFiles = async ({
  authAxios,
  selectedFile,
  serial_number,
  // new_visualizer,
}: any): Promise<any> => {
  if (!selectedFile) {
    throw new Error("No file provided for upload.");
  }
  try {
    const formData = new FormData();
    formData.append("file", selectedFile); // The server usually wants 'file'
    formData.append("serial_number", serial_number.toString());
    // formData.append("new_visualizer", new_visualizer);

    const { data } = await authAxios.post(
      `edge_pinch_update/update_retest_modules/`,
      formData,
    );
    return data;
  } catch (err: any) {
    const error =
      err?.response?.data?.error ||
      err?.response?.data?.message ||
      err?.message ||
      "Something went wrong";

    const details = err?.response?.data?.details;

    const detailMessage = Array.isArray(details) ? details.join(", ") : details;

    toast.error(detailMessage ? `${error}: ${detailMessage}` : error);
  }
};

export const smlFile = async ({
  authAxios,
  file,
}: PostFileProps): Promise<any> => {
  try {
    const formData = new FormData();

    formData.append("file", file, file.name);
    formData.append("hash_algorithm", "md5");
    formData.append("blob_container", "media");
    const { data } = await authAxios.post(`azure_files/`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return data;
  } catch (err) {
    const error =
      err.response.data instanceof Array
        ? err.response.data.join()
        : err.response.data;
    throw new Error(`Error while uploading file(s):${error}`);
  }
};

export const useSmlFile = () => {
  return useMutation(smlFile);
};

export const UseImageUpload = async (params: any): Promise<any> => {
  const { authAxios, formData } = params;

  const { data } = await authAxios.post(
    urls.reportWriterAgenda.workerorder_imageUpload,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
  return data;
};
export const UseworkOrderImageUpload = () => {
  return useMutation(UseImageUpload);
};

export const tiptapImageUpload = async (params: any): Promise<any> => {
  const { authAxios, formData } = params;

  const { data } = await authAxios.post(
    urls.reportWriterAgenda.tiptap_imageUpload,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );

  return data;

};
export const UseTiptapImageUpdate = () => {
  return useMutation(tiptapImageUpload);
};

export const usePostReportDocument = async ({
  authAxios,
  file,
  report_type_id,
  pqp_version,
}: PostParseDocumentProps): Promise<any> => {
  try {
    const formData = new FormData();

    formData.append("file", file, file.name);
    formData.append("report_type_id", report_type_id.toString());
    formData.append("version", pqp_version);

    const { data } = await authAxios.post(
      "writer_history/parse_doc/",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return data;
  } catch (err: any) {
    let errorMessage = "Error while parsing document";

    if (Array.isArray(err?.response?.data)) {
      errorMessage = err.response.data.join();
    } else if (
      typeof err?.response?.data === "object" &&
      err.response.data?.msg
    ) {
      errorMessage = err.response.data.msg;
    } else if (err?.message) {
      errorMessage = err.message;
    }

    throw new Error(errorMessage);
  }
};

export const usePostParseDocument = () => {
  return useMutation(usePostReportDocument);
};

//Upload Checklist
export const ChecklistUpload = async ({
  authAxios,
  file,
  report_type_definition_id,
  version,
}: PostChecklistProps): Promise<any> => {
  try {
    const formData = new FormData();

    formData.append("file", file, file.name);
    formData.append("report_type_definition_id", report_type_definition_id.toString());
    formData.append("version", version);

    const { data } = await authAxios.post(
      "checklist/upload-checklist/",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return data;
  } catch (err: any) {
    let errorMessage = "Error while parsing document";

    if (Array.isArray(err?.response?.data)) {
      errorMessage = err.response.data.join();
    } else if (
      typeof err?.response?.data === "object" &&
      err.response.data?.msg
    ) {
      errorMessage = err.response.data.msg;
    } else if (err?.message) {
      errorMessage = err.message;
    }

    throw new Error(errorMessage);
  }
};

export const UseChecklistUploadData = () => {
  return useMutation(ChecklistUpload);
};


// Add caption and onUploadProgress to your props interface
export interface PostLSDBCloudProps {
    authAxios: any;
    file: File;
    caption: string;
    onUploadProgress?: (progressEvent: any) => void;
}

export const UploadLSDBCloudFile = async ({
    authAxios,
    file,
    caption,
    onUploadProgress
}: PostLSDBCloudProps): Promise<any> => {
    try {
        const formData = new FormData();

        // Updated keys to match your required payload
        formData.append("image_path", file, file.name);
        formData.append("caption", caption || "");
        formData.append("uploaded_from", "web");

        const { data } = await authAxios.post(
            "module_intake_images/upload_uncatogorized_image/",
            formData,
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
                onUploadProgress, // Passes network progress back to the component
            }
        );

        return data;
    } catch (err: any) {
        let errorMessage = "Error while uploading file";

        if (Array.isArray(err?.response?.data)) {
            errorMessage = err.response.data.join();
        } else if (typeof err?.response?.data === "object" && err.response.data?.msg) {
            errorMessage = err.response.data.msg;
        } else if (err?.message) {
            errorMessage = err.message;
        }

        throw new Error(errorMessage);
    }
};

export const UseLSDBCloudUpload = () => {
    return useMutation(UploadLSDBCloudFile);
};